import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Hammer, FolderOpen, MessageSquare, User, LogOut, Plus, Menu, X, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo, Loader, StatusBadge } from "@/components/Brand";
import { formatCurrency, formatDate } from "@/lib/format";
import { fileDownloadUrl, humanSize } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["quotations", "My Quotations", FileText],
  ["projects", "My Projects", Hammer],
  ["documents", "Documents", FolderOpen],
  ["messages", "Messages", MessageSquare],
  ["profile", "Profile", User],
];

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [quotations, setQuotations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/quotations"), api.get("/projects")])
      .then(([q, p]) => { setQuotations(q.data.quotations); setProjects(p.data.projects); })
      .finally(() => setLoading(false));
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.map(([key, label, Icon]) => (
        <button key={key} onClick={() => { setTab(key); setMobileNav(false); }}
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            tab === key ? "bg-amber-500/10 text-amber-400" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"}`}
          data-testid={`client-nav-${key}`}>
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50 md:flex">
      {/* Sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col justify-between border-r border-slate-800/80 bg-[#0F1117] p-6 md:flex">
        <div>
          <Logo />
          <div className="mt-8"><NavItems /></div>
        </div>
        <button onClick={doLogout} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 hover:text-rose-400" data-testid="client-logout-btn">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>

      {/* Mobile top bar */}
      <div className="flex items-center justify-between border-b border-slate-800/80 p-4 md:hidden">
        <Logo size="text-lg" />
        <button onClick={() => setMobileNav(!mobileNav)} data-testid="client-mobile-menu">{mobileNav ? <X /> : <Menu />}</button>
      </div>
      {mobileNav && (
        <div className="border-b border-slate-800 bg-[#0F1117] p-4 md:hidden">
          <NavItems />
          <button onClick={doLogout} className="mt-2 flex items-center gap-3 px-3 py-2.5 text-sm text-rose-400"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      )}

      <main className="flex-1 px-4 py-8 sm:px-8">
        {loading ? <Loader /> : (
          <div className="mx-auto max-w-5xl">
            {tab === "dashboard" && <Overview user={user} quotations={quotations} navigate={navigate} setTab={setTab} />}
            {tab === "quotations" && <Quotations quotations={quotations} navigate={navigate} />}
            {tab === "projects" && <Projects projects={projects} />}
            {tab === "documents" && <Documents projects={projects} navigate={navigate} />}
            {tab === "messages" && <Empty icon={MessageSquare} title="Messages" body="Secure messaging with the EvoStruct team is coming soon." />}
            {tab === "profile" && <Profile user={user} />}
          </div>
        )}
      </main>
    </div>
  );
}

function Overview({ user, quotations, navigate, setTab }) {
  const latest = quotations[0];
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Welcome, {user.name}</h1>
      <p className="mt-1 text-sm text-slate-400">Here's a snapshot of your EvoStruct projects.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Quotations", quotations.length], ["Active", quotations.filter(q => !["Declined","Expired"].includes(q.status)).length],
          ["Accepted", quotations.filter(q => q.status === "Accepted").length],
          ["Est. value", formatCurrency(quotations.reduce((s, q) => s + (q.final_total || 0), 0))]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-800/80 bg-[#151820] p-5">
            <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
            <p className="mt-1 font-heading text-2xl font-bold text-slate-50">{v}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-800 bg-[#151820] p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Current quotation</h2>
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-slate-950 hover:bg-amber-500" data-testid="new-quote-btn">
            <Plus className="h-3.5 w-3.5" /> New quote
          </button>
        </div>
        {latest ? (
          <button onClick={() => navigate(`/quotation/${latest.id}`)} className="mt-4 flex w-full items-center justify-between rounded-xl border border-slate-800 bg-[#0d0f13] p-4 text-left hover:border-amber-500/40" data-testid="overview-latest-quote">
            <div>
              <p className="font-semibold">{latest.project_type_name} {latest.category_name}</p>
              <p className="text-xs text-slate-500">{latest.quotation_number} · {formatDate(latest.created_at)}</p>
            </div>
            <div className="text-right">
              <p className="font-mono font-bold text-amber-500">{formatCurrency(latest.final_total)}</p>
              <div className="mt-1"><StatusBadge status={latest.status} /></div>
            </div>
          </button>
        ) : (
          <p className="mt-4 text-sm text-slate-400">You have no quotations yet. <button onClick={() => navigate("/")} className="text-amber-400 hover:underline">Start one now</button>.</p>
        )}
      </div>
    </div>
  );
}

function Quotations({ quotations, navigate }) {
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">My Quotations</h1>
      {quotations.length === 0 ? (
        <p className="mt-4 text-sm text-slate-400">No quotations yet.</p>
      ) : (
        <div className="mt-6 space-y-3">
          {quotations.map((q) => (
            <button key={q.id} onClick={() => navigate(`/quotation/${q.id}`)}
              className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-[#151820] p-4 text-left hover:border-amber-500/40"
              data-testid={`quotation-row-${q.id}`}>
              <div>
                <p className="font-semibold">{q.project_type_name} {q.category_name}</p>
                <p className="text-xs text-slate-500">{q.quotation_number} · {formatDate(q.created_at)}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono font-bold text-amber-500">{formatCurrency(q.final_total)}</span>
                <StatusBadge status={q.status} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Projects({ projects }) {
  const navigate = useNavigate();
  if (projects.length === 0)
    return <Empty icon={Hammer} title="My Projects" body="When you accept a quotation and proceed with EvoStruct, your active project will appear here with timeline, stages, drawings and updates." />;
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">My Projects</h1>
      <div className="mt-6 space-y-3">
        {projects.map((p) => (
          <button key={p.id} onClick={() => navigate(`/project/${p.id}`)} className="w-full rounded-xl border border-slate-800 bg-[#151820] p-5 text-left hover:border-amber-500/40" data-testid={`project-row-${p.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg font-semibold">{p.name}</p>
              <StatusBadge status={p.status} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(p.stages || []).map((s) => (
                <span key={s.id} className={`rounded-full border px-2.5 py-1 text-xs ${s.name === p.status ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 text-slate-400"}`}>{s.name}</span>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-400">View timeline, photos &amp; documents →</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function Documents({ projects, navigate }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => {
    if (projects.length === 0) { setDocs([]); return; }
    Promise.all(projects.map((p) => api.get(`/projects/${p.id}`)
      .then((r) => r.data.documents.map((d) => ({ ...d, projectName: p.name })))
      .catch(() => []))).then((arr) => setDocs(arr.flat()));
  }, [projects]);
  if (projects.length === 0)
    return <Empty icon={FolderOpen} title="Documents" body="Your architectural drawings, contracts, certificates and project documents will appear here once your project is underway." />;
  if (!docs) return <Loader />;
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">Documents</h1>
      <p className="mt-1 text-sm text-slate-400">All documents across your projects. Open a project to upload.</p>
      <div className="mt-6 space-y-2">
        {docs.length === 0 && <p className="text-sm text-slate-500">No documents uploaded yet.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-800 bg-[#151820] p-4" data-testid={`doc-agg-${d.id}`}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800"><FileText className="h-5 w-5 text-amber-400" /></div>
              <div className="min-w-0">
                <p className="truncate font-medium">{d.original_filename}</p>
                <p className="text-xs text-slate-500">{d.projectName} · <span className="uppercase">{d.doc_type}</span> · {humanSize(d.size)}</p>
              </div>
            </div>
            <a href={fileDownloadUrl(d.id)} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-700 p-2 text-slate-300 hover:border-amber-500/50"><Download className="h-4 w-4" /></a>
          </div>
        ))}
      </div>
    </div>
  );
}

function Empty({ icon: Icon, title, body }) {
  return (
    <div className="evo-fade-up">
      <h1 className="font-heading text-3xl font-bold">{title}</h1>
      <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-800 bg-[#111318] px-6 py-16 text-center">
        <Icon className="h-10 w-10 text-slate-600" />
        <p className="mt-4 max-w-md text-sm text-slate-400">{body}</p>
      </div>
    </div>
  );
}

function Profile({ user }) {
  const { updateUser } = useAuth();
  const [form, setForm] = useState({
    name: user.name || "", phone: user.phone || "", email: user.email || "",
    line1: user.address?.line1 || "", line2: user.address?.line2 || "",
    city: user.address?.city || "", county: user.address?.county || "",
    postcode: user.address?.postcode || "", country: user.address?.country || "United Kingdom",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0d0f13] px-4 py-3 text-slate-100 outline-none focus:border-amber-500";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, email: form.email,
        address: { line1: form.line1, line2: form.line2, city: form.city, county: form.county, postcode: form.postcode, country: form.country } };
      if (form.password) payload.password = form.password;
      const { data } = await api.put("/auth/profile", payload);
      updateUser(data.user);
      toast.success("Profile updated");
      set("password", "");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="evo-fade-up max-w-2xl">
      <h1 className="font-heading text-3xl font-bold">Profile</h1>
      <div className="mt-6 space-y-4 rounded-2xl border border-slate-800 bg-[#151820] p-6">
        <input placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="profile-name-input" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="profile-email-input" />
          <input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="profile-phone-input" />
        </div>
        <input placeholder="Address line 1" value={form.line1} onChange={(e) => set("line1", e.target.value)} className={inp} />
        <input placeholder="Address line 2" value={form.line2} onChange={(e) => set("line2", e.target.value)} className={inp} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input placeholder="Town / City" value={form.city} onChange={(e) => set("city", e.target.value)} className={inp} />
          <input placeholder="County" value={form.county} onChange={(e) => set("county", e.target.value)} className={inp} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input placeholder="Postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className={inp} />
          <input placeholder="Country" value={form.country} onChange={(e) => set("country", e.target.value)} className={inp} />
        </div>
        <input type="password" placeholder="New password (leave blank to keep)" value={form.password} onChange={(e) => set("password", e.target.value)} className={inp} data-testid="profile-password-input" />
        <button onClick={save} disabled={saving} className="rounded-xl bg-amber-600 px-6 py-3 font-bold text-slate-950 hover:bg-amber-500 disabled:opacity-60" data-testid="profile-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
